import React, { useEffect, useState } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Icon from '@mui/material/Icon';
import { useAuthContext } from '../../../contexts/authContext';
import { featureFlagService } from '../../../services/featureFlags';
import { MDBox } from 'components/MDBox';
import { MDTypography, MDTypographyProps } from 'components/MDTypography';
import { SidenavCollapse } from './SidenavCollapse';
import { SidenavList } from './SidenavList';
import { SidenavItem } from './SidenavItem';
import { SidenavRoot } from './SidenavRoot';
// import { sidenavLogoLabel } from './styles/sidenav';
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from '../../../contexts/layoutContext';
// import { FFTheme } from 'assets/theme';
import { DrawerProps } from '@mui/material';
import { FFAvailablePath } from 'routes';

type Route = {
  type: string;
  name?: string | React.ReactNode;
  icon?: React.ReactNode;
  title?: string;
  collapse?: Route[];
  noCollapse?: boolean;
  key: string;
  href?: string;
  route?: string;
  allowedRoles?: string[]; // Add allowedRoles property
  featureFlag?: string; // Add featureFlag property
};

export type SidenavProps = DrawerProps & {
  color:
    | 'primary'
    | 'secondary'
    | 'info'
    | 'success'
    | 'warning'
    | 'error'
    | 'dark';
  brand?: string;
  brandName: string;
  routes: Route[];
};

function Sidenav({ color, brand, brandName, routes, ...rest }: SidenavProps) {
  const { user } = useAuthContext();
  const [openCollapse, setOpenCollapse] = useState<string | boolean>(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState<
    string | boolean
  >(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } =
    controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split('/').slice(1)[0];
  const items = pathname.split('/').slice(1);
  const itemParentName = items[1];
  let itemName = items[items.length - 1];

  if (FFAvailablePath.map((route) => route.path).includes(pathname)) {
    const foundRoute = FFAvailablePath.find((route) => route.path === pathname);
    itemName = foundRoute?.key || itemParentName;
  }

  let textColor = 'white';

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = 'dark';
  } else if (whiteSidenav && darkMode) {
    textColor = 'inherit';
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    const foundRoute = FFAvailablePath.find((route) => route.path === pathname);
    setOpenCollapse(foundRoute?.parentKey || foundRoute?.key || collapseName);
    setOpenNestedCollapse(foundRoute?.parentKey || itemParentName);
  }, [collapseName, itemParentName, pathname]);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : transparentSidenav
      );
      setWhiteSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : whiteSidenav
      );
    }

    window.addEventListener('resize', handleMiniSidenav);
    handleMiniSidenav();

    return () => window.removeEventListener('resize', handleMiniSidenav);
  }, [dispatch, location]);

  const renderNestedCollapse = (collapse: Route[]) => {
    const template = collapse.map(({ name, route, key, href, collapse }) => {
      // Check if this is a steerage sub-menu item
      const isSteerageSubItem = collapse && collapse.length > 0;
      const isActive =
        key === itemName ||
        (isSteerageSubItem && key === itemName) ||
        route === pathname;

      return href ? (
        <Link
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: 'none' }}
        >
          <SidenavItem name={name!} nested />
        </Link>
      ) : (
        <NavLink to={route!} key={key} style={{ textDecoration: 'none' }}>
          <SidenavItem name={name!} active={isActive ? 'true' : ''} nested />
        </NavLink>
      );
    });

    return template;
  };

  const renderCollapse = (collapses: Route[]) =>
    collapses.map(({ name, collapse, route, href, key }) => {
      let returnValue;

      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={name!}
            active={key === itemParentName ? 'isParent' : ''}
            open={openNestedCollapse === key}
            onClick={(el: any) =>
              openNestedCollapse === key &&
              el?.currentTarget?.classList.contains('MuiListItem-root')
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        // Check if this is a steerage sub-menu item
        const isSteerageSubItem =
          key === 'steerage-overview' || key === 'steerage-details';
        const isActive =
          key === itemName || (isSteerageSubItem && key === itemName);

        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: 'none' }}
          >
            <SidenavItem
              color={color}
              name={name!}
              active={isActive ? 'true' : ''}
            />
          </Link>
        ) : (
          <NavLink to={route!} key={key} style={{ textDecoration: 'none' }}>
            <SidenavItem
              color={color}
              name={name!}
              active={isActive ? 'true' : ''}
            />
          </NavLink>
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });

  const renderRoutes = routes.map(
    ({
      type,
      name,
      icon,
      title,
      collapse,
      noCollapse,
      key,
      href,
      route,
      allowedRoles,
      featureFlag,
    }) => {
      // Skip rendering if the route has allowed roles and the user doesn't have access
      if (
        allowedRoles &&
        allowedRoles.length > 0 &&
        !allowedRoles.includes(user?.role || '') &&
        user?.role !== 'super_user'
      ) {
        return null;
      }

      // Skip rendering if the route has a feature flag that is not enabled
      if (featureFlag) {
        const context = {
          tenantId: localStorage.getItem('tenantId') || undefined,
          userId: user?.id?.toString(),
          userRole: user?.role,
        };
        if (!featureFlagService.isEnabled(featureFlag, context)) {
          return null;
        }
      }
      let returnValue;

      if (type === 'collapse') {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: 'none' }}
            >
              <SidenavCollapse
                name={name!}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key}>
              <SidenavCollapse
                name={name!}
                icon={icon}
                noCollapse={noCollapse}
                active={
                  key === collapseName ||
                  (key === 'claims' && collapseName === '')
                }
              >
                {/* {collapse ? renderCollapse(collapse) : null} */}
                <SidenavItem
                  key={key}
                  color={color}
                  name={name!}
                  active={key === collapseName ? 'true' : ''}
                  open={true}
                />
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name!}
              icon={icon}
              active={false}
              open={openCollapse === key}
              onClick={() =>
                openCollapse === key
                  ? setOpenCollapse(false)
                  : setOpenCollapse(key)
              }
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === 'title') {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor as MDTypographyProps['color']}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={2}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === 'divider') {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      } else if (type === 'ff_item') {
        returnValue = (
          <NavLink to={route!} key={key} style={{ textDecoration: 'none' }}>
            <SidenavItem
              color={color}
              name={name!}
              icon={icon}
              active={key === itemName ? 'true' : ''}
            />
          </NavLink>
        );
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={2.5} pb={0} px={3} textAlign="center">
        <MDBox
          display={{ xs: 'block', xl: 'none' }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: 'pointer' }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: 'bold' }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && (
            <MDBox
              component="img"
              src={brand}
              alt="Brand"
              width="60%"
              style={{ display: 'block', margin: '0 auto' }}
            />
          )}
          {/* <MDBox
            width={!brandName && '100%'}
            sx={(theme: any) =>
              sidenavLogoLabel(theme as FFTheme, { miniSidenav })
            }
          >
            <MDTypography
              component="h6"
              variant="button"
              fontWeight="regular"
              color={textColor as MDTypographyProps['color']}
            >
              {brandName}
            </MDTypography>
          </MDBox> */}
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

export { Sidenav };
